import type { Role, UserStatus } from "@/lib/api-types";
import type { BadgeFill } from "@/lib/status-badge";

/**
 * Display text for enum *values* (as opposed to `field-labels.ts`, which
 * names *fields*) — a second, deliberately separate "one map" for a
 * different axis of the same rule: `GET /meta/enums` (AD-2) is the source
 * of truth for which roles/statuses *exist*, never this file, but once a
 * value exists somewhere, it needs exactly one plain-language rendering,
 * defined once. A filter dropdown's option list is still built by mapping
 * over the live `/meta/enums` response, not by iterating this object's
 * keys — this file only supplies the label for whichever values that
 * response actually names.
 */
export const ROLE_LABELS: Record<Role, string> = {
  owner: "Owner",
  member: "Member",
  read_only: "Read-only",
};

export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  invited: "Invited",
  active: "Active",
};

/**
 * Story 3.8: `AlertRuleResource.cadence` is a plain `string` (AD-22 fixes
 * four legal values in the backend, `app/repo/models/alert_rule.py`'s own
 * `CADENCES`), not a narrow generated union like `Role`/`UserStatus` —
 * there is no `GET /meta/enums` entry for it (a small, code-fixed set, not
 * something an Owner ever adds to). Falls back to the raw value rather than
 * throwing (unlike `labelFor`'s field-name map): a rule's cadence is always
 * one of AD-22's four, but this map staying honestly non-exhaustive is
 * safer than a screen crashing outright if that ever drifts.
 */
export const CADENCE_LABELS: Record<string, string> = {
  once: "One time",
  daily: "Daily",
  every_3_days: "Every 3 days",
  weekly: "Weekly",
};

/**
 * Story 6.1: `TenantResource.status` is a plain `string` (spec judgement
 * call 3, mirroring `Unit.intent`'s identical treatment — `app/repo/models/
 * tenant.py`'s own `TENANT_STATUSES` is the one, code-owned source of truth,
 * VC-6.1-12), not a narrow generated union — there is no `GET /meta/enums`
 * entry for it. Mirrors `CADENCE_LABELS`'s exact shape/reasoning above.
 */
export const TENANT_STATUS_LABELS: Record<string, string> = {
  current: "Current",
  notice_given: "Notice given",
  former: "Former",
};

/**
 * Story 15.8's own Tenant record-page status pill (spec Intent: "a status
 * pill next to the record's own name"). None of `current`/`notice_given`/
 * `former` matches any of the four documented `Badge fill` meanings
 * (EXPERIENCE.md's own status-to-badge table: `overdue` = past due,
 * `attention` = approaching a threshold, `settled` = completed/paid/signed)
 * — mirrors `lib/status-badge.ts::USER_STATUS_FILL`'s identical reasoning
 * for `invited`/`active`: absent a specified urgency, `neutral` is the
 * honest default for all three, not an invented one.
 */
export const TENANT_STATUS_FILL: Record<string, BadgeFill> = {
  current: "neutral",
  notice_given: "neutral",
  former: "neutral",
};

/**
 * Story 6.5: `LeaseResource.status` is a plain `string` (`app/repo/models/
 * lease.py`'s own `LeaseStatus`, imported nowhere on the frontend — AD-2's
 * single source of truth lives in the backend, this is only its display
 * text), not a narrow generated union. Mirrors `TENANT_STATUS_LABELS`'s
 * exact shape/reasoning above; the seven values themselves come from
 * `LeaseStatus`, the generated union already exported by `api-types.ts`
 * (used only for exhaustiveness elsewhere, never re-derived here).
 */
export const LEASE_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  active: "Active",
  expiring: "Expiring",
  holding_over: "Holding over",
  renewed: "Renewed",
  ended: "Ended",
  terminated_early: "Terminated early",
};

/**
 * Story 11.1: `RenewalResource.status` — mirrors `LEASE_STATUS_LABELS`'s
 * exact shape/reasoning; the nine values come from `app/domain/renewals/
 * stages.py::RenewalStatus`.
 */
export const RENEWAL_STATUS_LABELS: Record<string, string> = {
  not_started: "Not started",
  owner_decided: "Owner decided",
  tenant_contacted: "Tenant contacted",
  awaiting_tenant_response: "Awaiting tenant response",
  negotiating: "Negotiating",
  agreed_renewing: "Agreed – renewing",
  agreed_vacating: "Agreed – vacating",
  signed: "Signed",
  lapsed_to_month_to_month: "Lapsed to month-to-month",
};

/** Story 11.1: `RenewalResource.owner_intent` (`app/repo/models/renewal.py
 * ::OWNER_INTENTS`). */
export const RENEWAL_OWNER_INTENT_LABELS: Record<string, string> = {
  renew: "Renew",
  renew_with_increase: "Renew with increase",
  do_not_renew: "Do not renew",
  undecided: "Undecided",
};

/** Story 11.1: the one legal next event per `RenewalResource.status`,
 * mirroring `app/domain/renewals/transitions.py`'s own `_TRANSITIONS`
 * table for every non-branching edge — `negotiating` has two legal next
 * events (the AC's own owner-intent branch), so it is handled separately
 * by the page itself rather than forced into this one-event-per-status
 * shape. `signed`/`lapsed_to_month_to_month` are terminal: no entry. */
export const RENEWAL_NEXT_EVENT: Record<string, { event: string; label: string } | undefined> = {
  not_started: { event: "owner_decide", label: "Mark owner decided" },
  owner_decided: { event: "contact_tenant", label: "Mark tenant contacted" },
  tenant_contacted: { event: "await_tenant_response", label: "Mark awaiting tenant response" },
  awaiting_tenant_response: { event: "begin_negotiating", label: "Begin negotiating" },
};

/**
 * Story 11.3: the renewal board's own fixed column set, in the stage
 * machine's real forward order (AC: "Not started -> Owner decided ->
 * Tenant contacted -> Awaiting tenant response -> Negotiating -> (Agreed –
 * renewing | Agreed – vacating) -> Signed") — a real, fixed set, never
 * invented separately from `RenewalResource.status`'s own nine-value
 * vocabulary (`app/domain/renewals/stages.py::RenewalStatus`).
 *
 * `lapsed_to_month_to_month` is deliberately absent: the AC's own column
 * list stops at `Signed` and never names it, and nothing in this codebase
 * transitions a renewal into it yet (`transitions.py`'s own "legal but no
 * real caller" precedent, Story 11.1) — a renewal that somehow reached it
 * would not render as a card on the board at all rather than inventing a
 * ninth column the frozen spec never asked for.
 */
export const RENEWAL_BOARD_COLUMNS: readonly string[] = [
  "not_started",
  "owner_decided",
  "tenant_contacted",
  "awaiting_tenant_response",
  "negotiating",
  "agreed_renewing",
  "agreed_vacating",
  "signed",
];

/**
 * Story 11.3: the tap-to-move menu's own legal next event(s) for a card's
 * current status — mirrors `RenewalRecord.tsx`'s own `RENEWAL_NEXT_EVENT`/
 * branching-at-`Negotiating` logic exactly (AC's own explicit words: a
 * card's menu lists "only the legal next event(s) for its current status...
 * never a free choice of any column"). `Negotiating` returns both branches;
 * `Agreed – renewing`/`Agreed – vacating`/`Signed` return none — mirrors
 * `RenewalRecord.tsx`'s own separate `isAgreed` Sign button, which calls
 * `POST /renewals/{id}/sign` directly, never the generic `PATCH .../status`
 * event route this menu fires (VC-11.3-04's second assertion: this menu
 * never offers `sign`, on any status, at all).
 */
export function renewalBoardMenuItems(status: string): { event: string; label: string }[] {
  if (status === "negotiating") {
    return [
      { event: "agree_renewing", label: "Agree – renewing" },
      { event: "agree_vacating", label: "Agree – vacating" },
    ];
  }
  const next = RENEWAL_NEXT_EVENT[status];
  return next ? [next] : [];
}

/**
 * Story 12.1's own `EquipmentResource.status` (`app/repo/models/equipment.py::
 * EQUIPMENT_STATUSES`) — a plain `string`, not a narrow generated union
 * (mirrors `AlertRuleResource.cadence`'s own identical shape/reasoning
 * immediately above: no `GET /meta/enums` entry exists for this either).
 * `replaced`/`removed` are real, storable values no UI drives yet (Story
 * 12.5's own future job) — labeled here anyway so a future record showing
 * one never falls back to a raw snake_case string.
 */
export const EQUIPMENT_STATUS_LABELS: Record<string, string> = {
  details_needed: "Details needed",
  active: "Active",
  replaced: "Replaced",
  removed: "Removed",
};
